import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../../auth/AuthContext";
import { toast } from "sonner";
import { Building2, Lock, ShieldCheck } from "lucide-react";

const EMPTY = {
  account_holder_name: "",
  bank_name: "",
  account_type: "checking",
  routing_number: "",
  institution_number: "",
  transit_number: "",
  account_number: "",
  country: "CA",
};

export default function HostPayoutForm({ profile, configured, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const isCanada = form.country === "CA";
  const profileIsCanada = (profile?.country || "CA") === "CA";

  useEffect(() => {
    if (!profile) {
      setForm(EMPTY);
      return;
    }
    setForm({
      account_holder_name: profile.account_holder_name || "",
      bank_name: profile.bank_name || "",
      account_type: profile.account_type || "checking",
      routing_number: "",
      institution_number: "",
      transit_number: "",
      account_number: "",
      country: profile.country || "CA",
    });
  }, [profile]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (isCanada) {
      if (!form.institution_number.match(/^\d{3}$/)) {
        toast.error("Institution number must be 3 digits");
        return;
      }
      if (!form.transit_number.match(/^\d{5}$/)) {
        toast.error("Transit number must be 5 digits");
        return;
      }
    } else if (!form.routing_number.match(/^\d{9}$/)) {
      toast.error("Routing number must be 9 digits");
      return;
    }
    if (!form.account_number.match(/^\d{4,17}$/)) {
      toast.error("Account number must be 4–17 digits");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        account_holder_name: form.account_holder_name,
        bank_name: form.bank_name,
        account_type: form.account_type,
        account_number: form.account_number,
        country: form.country,
      };
      if (isCanada) {
        payload.institution_number = form.institution_number;
        payload.transit_number = form.transit_number;
      } else {
        payload.routing_number = form.routing_number;
      }
      const r = await axios.put(`${API}/host/payout-profile`, payload, { withCredentials: true });
      toast.success("Banking details saved");
      onSaved?.(r.data?.profile);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save banking details");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="host-payout-form">
      {configured && profile && (
        <div
          className="px-4 py-3 rounded-lg flex items-start gap-3 text-sm"
          style={{ background: "rgba(22,163,74,0.08)", border: "1px solid rgba(22,163,74,0.25)" }}
          data-testid="host-payout-on-file"
        >
          <ShieldCheck size={18} className="text-[var(--zc-success)] shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold text-[var(--zc-text)]">Payout account on file</div>
            <div className="text-[var(--zc-text-2)] mt-1">
              {profile.bank_name} · {profile.account_type} ····{profile.account_number_last4}
              {!profileIsCanada && profile.routing_number_last4
                ? ` · routing ····${profile.routing_number_last4}`
                : ""}
              {profileIsCanada && profile.institution_number_last3
                ? ` · institution ···${profile.institution_number_last3}`
                : ""}
              {profileIsCanada && profile.transit_number_last4
                ? ` · transit ····${profile.transit_number_last4}`
                : ""}
            </div>
            <div className="zc-hint mt-1">
              {isCanada
                ? "Re-enter institution, transit, and account numbers below to update."
                : "Re-enter routing and account numbers below to update."}
            </div>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <label htmlFor="payout-country" className="text-xs text-[var(--zc-text-3)]">Country</label>
          <select
            id="payout-country"
            className="zc-input h-11 px-3 w-full text-sm mt-1"
            value={form.country}
            onChange={(e) => set("country", e.target.value)}
            data-testid="payout-country"
          >
            <option value="CA">Canada</option>
            <option value="US">United States</option>
          </select>
        </div>
        <div className="md:col-span-2">
          <label htmlFor="payout-holder" className="text-xs text-[var(--zc-text-3)]">Account holder name</label>
          <input
            id="payout-holder"
            className="zc-input h-11 px-3 w-full text-sm mt-1"
            value={form.account_holder_name}
            onChange={(e) => set("account_holder_name", e.target.value)}
            placeholder="Legal name on the account"
            required
            data-testid="payout-holder"
          />
        </div>
        <div>
          <label htmlFor="payout-bank" className="text-xs text-[var(--zc-text-3)]">Bank name</label>
          <input
            id="payout-bank"
            className="zc-input h-11 px-3 w-full text-sm mt-1"
            value={form.bank_name}
            onChange={(e) => set("bank_name", e.target.value)}
            placeholder={isCanada ? "TD, RBC, Scotiabank, …" : "Your bank name"}
            required
            data-testid="payout-bank"
          />
        </div>
        <div>
          <label htmlFor="payout-type" className="text-xs text-[var(--zc-text-3)]">Account type</label>
          <select
            id="payout-type"
            className="zc-input h-11 px-3 w-full text-sm mt-1"
            value={form.account_type}
            onChange={(e) => set("account_type", e.target.value)}
            data-testid="payout-type"
          >
            <option value="checking">Checking</option>
            <option value="savings">Savings</option>
          </select>
        </div>
        {isCanada ? (
          <>
            <div>
              <label htmlFor="payout-institution" className="text-xs text-[var(--zc-text-3)]">Institution number</label>
              <input
                id="payout-institution"
                className="zc-input h-11 px-3 w-full text-sm mt-1 font-mono"
                value={form.institution_number}
                onChange={(e) => set("institution_number", e.target.value.replace(/\D/g, "").slice(0, 3))}
                placeholder="3 digits"
                inputMode="numeric"
                required
                data-testid="payout-institution"
              />
            </div>
            <div>
              <label htmlFor="payout-transit" className="text-xs text-[var(--zc-text-3)]">Transit number</label>
              <input
                id="payout-transit"
                className="zc-input h-11 px-3 w-full text-sm mt-1 font-mono"
                value={form.transit_number}
                onChange={(e) => set("transit_number", e.target.value.replace(/\D/g, "").slice(0, 5))}
                placeholder="5 digits"
                inputMode="numeric"
                required
                data-testid="payout-transit"
              />
            </div>
          </>
        ) : (
          <div>
            <label htmlFor="payout-routing" className="text-xs text-[var(--zc-text-3)]">Routing number (ABA)</label>
            <input
              id="payout-routing"
              className="zc-input h-11 px-3 w-full text-sm mt-1 font-mono"
              value={form.routing_number}
              onChange={(e) => set("routing_number", e.target.value.replace(/\D/g, "").slice(0, 9))}
              placeholder="9 digits"
              inputMode="numeric"
              required
              data-testid="payout-routing"
            />
          </div>
        )}
        <div>
          <label htmlFor="payout-account" className="text-xs text-[var(--zc-text-3)]">Account number</label>
          <input
            id="payout-account"
            className="zc-input h-11 px-3 w-full text-sm mt-1 font-mono"
            value={form.account_number}
            onChange={(e) => set("account_number", e.target.value.replace(/\D/g, "").slice(0, 17))}
            placeholder={configured ? "Enter to update" : "Account number"}
            inputMode="numeric"
            required
            data-testid="payout-account"
          />
        </div>
      </div>

      <div className="flex items-start gap-2 zc-hint">
        <Lock size={12} className="shrink-0 mt-0.5" />
        <span>
          Used for weekly bank transfer (direct deposit) payouts from car rentals and parking. Stored securely; only the last four digits are shown after save.
        </span>
      </div>

      <button
        type="submit"
        disabled={saving}
        className="px-5 h-11 zc-btn-primary text-sm inline-flex items-center gap-2 disabled:opacity-60"
        data-testid="payout-save"
      >
        <Building2 size={14} />
        {saving ? "Saving…" : configured ? "Update banking details" : "Save banking details"}
      </button>
    </form>
  );
}
